import {transparentize} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AttachmentModel} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {MessageAttachment} from '../attachments/messageAttachment';

/*
 * Props.
 */

interface MessageViewerAttachmentsProps {
  attachments: ReadonlyArray<AttachmentModel>;
  timelineVariation: TimelineVariationsEnum;
  shouldScroll: boolean;
  onDownloadAllClick: () => void;
}

interface StyledWrapperDivProps {
  $timelineVariation: TimelineVariationsEnum;
}

interface StyledAttachmentsDivProps {
  $timelineVariation: TimelineVariationsEnum;
  $shouldScroll: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div<StyledWrapperDivProps>`
  position: relative;
  display: flex;
  flex-direction: column;

  ${(p) => maybeAddWrapperBorder(p.$timelineVariation, p.theme.greys.shade30)}
`;

const StyledHeaderDiv = styled.div`
  position: relative;
  height: 45px;
`;

const StyledTitleDiv = styled.div`
  position: absolute;
  left: 16px;
  top: 14px;
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
`;

const StyledAllButtonWrapper = styled(ChromelessButton)`
  position: absolute;
  right: 16px;
  top: 8px;
  width: 116px;
  height: 30px;

  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.medium};

  padding: 7px 14px;

  &:hover {
    background: ${(p) => p.theme.greys.shade20};
    border-radius: 100px;
  }
`;

const StyledAllButton = styled.div`
  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.medium};
  ${center()};
`;

const StyledAttachmentsDiv = styled.div<StyledAttachmentsDivProps>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${(p) => addAttachmentsDivStyles(p.$timelineVariation)}
  ${(p) => addScrollingStyles(p.$shouldScroll)}
`;

const StyledOverlayGradient = styled.div`
  position: absolute;
  background-image: linear-gradient(
    ${(p) => transparentize(1, p.theme.message.background)},
    ${(p) => p.theme.message.background}
  );
  bottom: 8px;
  height: 8px;
  width: 100%;
`;

/*
 * Component.
 */

export const MessageViewerAttachments: FC<MessageViewerAttachmentsProps> = (props) => {
  const maybeRenderHeader = () => {
    const {attachments, timelineVariation, onDownloadAllClick} = props;

    if (timelineVariation === TimelineVariationsEnum.SYNCHRONOUS) {
      return null;
    }

    return (
      <StyledHeaderDiv>
        <StyledTitleDiv>
          <Plural
            id="message-attachments-title-new"
            value={attachments.length}
            one="# Attachment"
            other="# Attachments"
          />
        </StyledTitleDiv>
        <StyledAllButtonWrapper onClick={onDownloadAllClick}>
          <StyledAllButton>
            <Trans id="message-attachments-downloadall-new">Download All</Trans>
          </StyledAllButton>
        </StyledAllButtonWrapper>
      </StyledHeaderDiv>
    );
  };

  const renderAttachments = () => {
    const {attachments, timelineVariation} = props;
    const hasLargeAttachmentView = attachments.some((attachment) => attachment.hasThumbnail);
    const attachmentRenders = attachments.map((attachment) => (
      <MessageAttachment
        key={attachment.cid}
        attachment={attachment}
        hasLargeAttachmentView={hasLargeAttachmentView}
      />
    ));

    // Wrap and return.
    return (
      <StyledAttachmentsDiv
        $timelineVariation={timelineVariation}
        $shouldScroll={Boolean(props.shouldScroll)}
      >
        {attachmentRenders}
      </StyledAttachmentsDiv>
    );
  };

  // If there are no attachments, nothing to render.
  const {attachments, timelineVariation} = props;
  if (attachments.length === 0) {
    return null;
  }

  return (
    <StyledWrapperDiv $timelineVariation={timelineVariation}>
      {maybeRenderHeader()}
      {renderAttachments()}
      {timelineVariation !== TimelineVariationsEnum.SYNCHRONOUS && <StyledOverlayGradient />}
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeAddWrapperBorder(timelineVariation: TimelineVariationsEnum, color: string) {
  if (timelineVariation === TimelineVariationsEnum.SYNCHRONOUS) {
    return '';
  }

  return css`
    border-bottom: 1px solid ${color};
  `;
}

function addAttachmentsDivStyles(timelineVariation: TimelineVariationsEnum) {
  if (timelineVariation === TimelineVariationsEnum.SYNCHRONOUS) {
    return '';
  }

  return css`
    padding-left: 16px;
    margin-right: 7px;
    margin-bottom: 8px;
    padding-bottom: 8px;
  `;
}

function addScrollingStyles(shouldScroll: boolean) {
  if (!shouldScroll) {
    return '';
  }

  return css`
    max-height: 180px;
    overflow: auto;
  `;
}
